// Gom việc của QC thành MỘT nhóm menu tên "Chất lượng" — chỉ cho vai `qc`.
//
// Vì sao cần (khảo sát hành trình QC 18/09/2026, chỗ vướng V9): QC khối ảnh
// thấy 21 mục menu, mà việc thật của họ đúng 4 mục — Chấm điểm, Chất lượng
// nhân sự, Đánh Giá, Khiếu Nại. Ba cái đầu nằm lẫn trong "Studio & Hậu kỳ"
// cạnh Sửa ảnh / Đơn in / Bảng suất, cái thứ tư lại rơi sang "Chăm sóc khách".
// Không nhóm nào tên "Chất lượng". Nguyên do: menu QC dựng bằng cách chép menu
// trưởng phòng rồi bỏ mấy trang tiền, nên nó mang hình dạng ngày làm việc của
// trưởng phòng chứ không phải của QC.
//
// ⚠️ Vì sao SẮP XẾP LÚC CHẠY chứ không sửa thẳng NAV_GROUPS: thứ tự 8 nhóm và
// chỗ đứng của từng mục trong đó là CEO chốt 03/09/2026, và riêng "Chấm điểm"
// đứng cạnh "Chất lượng nhân sự" là CEO chốt 01/09/2026 kèm lý do (nó vốn là
// một thẻ chìm trong hồ sơ cá nhân, người bận bỏ qua, mà họ không chấm thì
// nhân viên MẤT ĐIỂM OAN). Kéo bốn mục đó ra khỏi NAV_GROUPS là đổi menu của
// MỌI vai — trưởng phòng, CSKH, giám đốc — để chữa một chỗ vướng của đúng một
// vai. Hàm này chỉ đổi thứ QC nhìn thấy; mọi vai khác nhận lại đúng mảng cũ.
import { qcKindOf } from "@/lib/auth/role-labels";
import type { NavGroup } from "./nav-items";

/**
 * Bốn mục việc của QC, xếp theo NGÀY LÀM VIỆC của họ chứ không theo bảng chữ
 * cái: mở máy là vào hàng đợi chấm (`performance_review`), chấm xong tra lại
 * người (`qc_board`), rồi mới tới hai màn mở thưa hơn.
 *
 * Mục nào QC không được cấp quyền thì tự khuyết — hàm chỉ SẮP XẾP LẠI những
 * mục đã qua bộ lọc quyền, không cấp thêm cửa nào.
 */
// "branch_scores" thêm 28/09/2026 (QĐ 0246): điểm cơ sở là kết quả trực tiếp
// của các lượt Kiểm cơ sở QC vừa chấm, nên đứng ngay sau hai màn chấm.
// "qc_overview" thêm 05/10/2026 (QĐ 0297): Bảng QC cho mỗi QC thấy phần mình
// và phần đồng nghiệp — đứng sau các màn chấm, trước hai màn mở thưa.
// "qc_desk" thêm 03/11/2026 (QĐ 0521): Bàn làm việc QC đứng ĐẦU nhóm — mở
// máy là vào đó trước, rồi mới tới từng màn chấm.
// "qc_raw_review" (CEO chốt 2a, 03/11/2026): ngay sau Chấm điểm — QC sửa hình và
// (vòng 3 · 3a) QC chụp ảnh. "qc_idphoto": ngay sau đó, chỉ QC chụp ảnh.
// "qc_journey" (vòng 4): ngay sau Chấm điểm — chỉ QC CSKH.
const QUALITY_ITEM_IDS = ["qc_desk", "performance_review", "qc_journey", "qc_raw_review", "qc_idphoto", "qc_board", "branch_scores", "qc_overview", "quality", "complaints"] as const;

// Mục không đúng nghề, bỏ khỏi menu QC (CEO vòng 2 · 2a, 03/11/2026, QĐ 0521).
// Chỉ bỏ khỏi MENU: đường dẫn vẫn mở được từ thông báo / nút ở màn khác.
// Giờ công của chính QC vẫn ở "Của tôi" (khối checkin), nên bỏ "Chấm công"
// cả cơ sở không làm QC mất lối xem công của mình.
export const QC_HIDDEN_ITEM_IDS = [
  "branch_shifts",
  "staff_checkins",
  "staff",
  "staff_productivity",
  "performance_admin",
  "costumes",
  "branch_kpi",
] as const;
// QC CSKH bỏ thêm ba mục ca ekip (họ rơi xuống khoá trang hẹp `qc`).
export const QC_CSKH_EXTRA_HIDDEN_IDS = ["set_board", "ekip_shifts", "dispatch_board"] as const;

/** Nhóm mới chen vào NGAY SAU "Tổng quan" — trên mọi nhóm việc khác. */
const INSERT_AFTER_GROUP_ID = "overview";

export function groupQualityForQc(
  groups: NavGroup[],
  role: string | undefined,
  department?: string | null,
): NavGroup[] {
  if ((role ?? "").toLowerCase().trim() !== "qc") return groups;
  const hidden = new Set<string>(
    qcKindOf(department) === "cskh" ? [...QC_HIDDEN_ITEM_IDS, ...QC_CSKH_EXTRA_HIDDEN_IDS] : QC_HIDDEN_ITEM_IDS,
  );

  const wanted = new Set<string>(QUALITY_ITEM_IDS);
  const picked = QUALITY_ITEM_IDS.map((id) =>
    groups.flatMap((g) => g.items).find((i) => i.id === id),
  ).filter((i) => i !== undefined);
  if (picked.length === 0) return groups;

  const rest = groups
    .map((g) => ({ ...g, items: g.items.filter((i) => !wanted.has(i.id) && !hidden.has(i.id)) }))
    .filter((g) => g.items.length > 0);

  const quality: NavGroup = { id: "qc_quality", label: "Chất lượng", items: picked };
  // 27/09/2026 (CEO chốt 2a) các nhóm còn lại từng gom vào ô "Khác" thu gọn.
  // Từ 03/11/2026 (QĐ 0521) THÔI thu gọn: mục không đúng nghề đã bỏ ở trên, phần
  // còn lại đều là việc của QC — đúng bản vẽ CEO duyệt, các nhóm mở sẵn.
  const at = rest.findIndex((g) => g.id === INSERT_AFTER_GROUP_ID);
  // Không thấy "Tổng quan" (QC chưa được cấp Dashboard lẫn Hiệu suất) thì nhóm
  // này lên đầu — vẫn đúng ý "việc của họ nằm trên cùng".
  const cut = at === -1 ? 0 : at + 1;
  return [...rest.slice(0, cut), quality, ...rest.slice(cut)];
}
